import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Phone, Search, CreditCard, CheckCircle, Calendar, Users, DollarSign, ArrowRight, Lock, ArrowLeft, UserCheck } from 'lucide-react';

const QuoteVerificationSystem = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [currentView, setCurrentView] = useState('search'); // 'search', 'quote', 'payment', 'confirmed'
  const [quoteData, setQuoteData] = useState(null);
  const [cardData, setCardData] = useState({
    number: '',
    expiry: '',
    cvv: '',
    name: ''
  });

  // Datos de ejemplo para diferentes números de teléfono
  const mockQuotes = {
    '3334801569': {
      id: 'COT-2024-001',
      persons: 4,
      startDate: '15 de Diciembre, 2024',
      endDate: '18 de Diciembre, 2024',
      destination: 'Disney World Orlando',
      total: 2450.00,
      isPaid: false,
      details: 'Paquete familiar 4 días / 3 noches'
    },
    '5551234567': {
      id: 'COT-2024-002',
      persons: 2,
      startDate: '22 de Enero, 2025',
      endDate: '26 de Enero, 2025',
      destination: 'Cancún Resort',
      total: 1850.00,
      isPaid: true,
      details: 'Luna de miel 5 días / 4 noches'
    },
    '7778889999': {
      id: 'COT-2024-003',
      persons: 6,
      startDate: '10 de Febrero, 2025',
      endDate: '12 de Febrero, 2025',
      destination: 'Las Vegas',
      total: 3200.00,
      isPaid: false,
      details: 'Grupo de amigos 3 días / 2 noches'
    }
  };

  const handleSearch = () => {
    const quote = mockQuotes[phoneNumber];
    if (quote) {
      setQuoteData(quote);
      setCurrentView('quote');
    } else {
      alert('No se encontró ninguna cotización asociada a este número de teléfono. Por favor verifica el número o contacta a nuestro equipo de soporte.');
    }
  };

  const handlePayment = () => {
    // Simular procesamiento de pago
    setTimeout(() => {
      setQuoteData({...quoteData, isPaid: true});
      setCurrentView('confirmed');
    }, 2000);
  };

  const handleCardChange = (field, value) => {
    setCardData({...cardData, [field]: value});
  };

  const resetSystem = () => {
    setPhoneNumber('');
    setQuoteData(null);
    setCurrentView('search');
    setCardData({ number: '', expiry: '', cvv: '', name: '' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50">
      <div className="pt-24 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Botones de navegación con más espacio desde arriba */}
          <div className="flex justify-between items-center mb-12">
            <Link
              to="/"
              className="flex items-center text-gray-700 hover:text-pink-500 transition-colors bg-white px-4 py-2 rounded-lg shadow-md hover:shadow-lg"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver al Inicio
            </Link>
            
            <Link
              to="/login"
              className="flex items-center bg-gray-800 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors shadow-md hover:shadow-lg"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <UserCheck className="w-4 h-4 mr-2" />
              Login Admin
            </Link>
          </div>
          <AnimatePresence mode="wait">
            {/* Vista de Búsqueda */}
            {currentView === 'search' && (
              <motion.div
                key="search"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="text-center"
              >
                <motion.div
                  className="w-24 h-24 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-8 shadow-2xl"
                  whileHover={{ rotate: 360, scale: 1.1 }}
                  transition={{ duration: 0.6 }}
                >
                  <Phone className="w-12 h-12 text-white" />
                </motion.div>

                <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Verificar Mi Cotización
                </h1>

                <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                  Ingresa tu número de teléfono para buscar y gestionar tu reservación
                </p>

                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md mx-auto">
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Número de Teléfono
                    </label>
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="Ej: 3334801569"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                    />
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleSearch}
                    disabled={!phoneNumber}
                    className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transition-all duration-300 flex items-center justify-center"
                  >
                    <Search className="w-5 h-5 mr-2" />
                    Buscar Cotización
                  </motion.button>
                </div>

                <div className="mt-8 text-sm text-gray-500">
                  <p>Números de prueba: 3334801569, 5551234567, 7778889999</p>
                </div>
              </motion.div>
            )}

            {/* Vista de Cotización */}
            {currentView === 'quote' && quoteData && (
              <motion.div
                key="quote"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="max-w-2xl mx-auto"
              >
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                  <div className="bg-gradient-to-r from-green-500 to-blue-600 p-6 text-white">
                    <div className="flex items-center justify-between">
                      <h2 className="text-2xl font-bold">Tu Cotización</h2>
                      <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                        quoteData.isPaid ? 'bg-green-200 text-green-800' : 'bg-yellow-200 text-yellow-800'
                      }`}>
                        {quoteData.isPaid ? 'PAGADO' : 'PENDIENTE'}
                      </div>
                    </div>
                    <p className="text-green-100 mt-1">#{quoteData.id}</p>
                  </div>

                  <div className="p-6 space-y-4">
                    <div className="flex items-center space-x-3">
                      <Calendar className="w-5 h-5 text-blue-500" />
                      <div>
                        <p className="font-medium">Fechas de Viaje</p>
                        <div className="text-gray-600">
                          <p className="text-sm text-gray-500">Inicio:</p>
                          <p className="font-medium">{quoteData.startDate}</p>
                          <p className="text-sm text-gray-500 mt-1">Fin:</p>
                          <p className="font-medium">{quoteData.endDate}</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <Users className="w-5 h-5 text-blue-500" />
                      <div>
                        <p className="font-medium">Número de Personas</p>
                        <p className="text-gray-600">{quoteData.persons} personas</p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <DollarSign className="w-5 h-5 text-blue-500" />
                      <div>
                        <p className="font-medium">Total a Pagar</p>
                        <p className="text-2xl font-bold text-green-600">${quoteData.total.toLocaleString()}</p>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-medium mb-2">Detalles del Paquete</h3>
                      <p className="text-gray-600">{quoteData.destination}</p>
                      <p className="text-sm text-gray-500">{quoteData.details}</p>
                    </div>
                  </div>

                  <div className="p-6 bg-gray-50 flex space-x-4">
                    <button
                      onClick={resetSystem}
                      className="flex-1 bg-gray-300 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-400 transition-colors"
                    >
                      Buscar Otra
                    </button>
                    
                    {quoteData.isPaid ? (
                      <div className="flex-1 bg-green-500 text-white py-3 px-6 rounded-lg font-medium flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Ya Pagado
                      </div>
                    ) : (
                      <button
                        onClick={() => setCurrentView('payment')}
                        className="flex-1 bg-gradient-to-r from-orange-500 to-red-600 text-white py-3 px-6 rounded-lg font-medium hover:shadow-lg transition-all duration-300 flex items-center justify-center"
                      >
                        <CreditCard className="w-5 h-5 mr-2" />
                        Pagar Ahora
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Vista de Pago */}
            {currentView === 'payment' && (
              <motion.div
                key="payment"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="max-w-2xl mx-auto"
              >
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                  <div className="bg-gradient-to-r from-orange-500 to-red-600 p-6 text-white">
                    <h2 className="text-2xl font-bold flex items-center">
                      <Lock className="w-6 h-6 mr-3" />
                      Pago Seguro
                    </h2>
                    <p className="text-orange-100 mt-1">Total: ${quoteData?.total.toLocaleString()}</p>
                  </div>

                  <div className="p-6 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Número de Tarjeta
                      </label>
                      <input
                        type="text"
                        value={cardData.number}
                        onChange={(e) => handleCardChange('number', e.target.value)}
                        placeholder="1234 5678 9012 3456"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Fecha de Expiración
                        </label>
                        <input
                          type="text"
                          value={cardData.expiry}
                          onChange={(e) => handleCardChange('expiry', e.target.value)}
                          placeholder="MM/YY"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          CVV
                        </label>
                        <input
                          type="text"
                          value={cardData.cvv}
                          onChange={(e) => handleCardChange('cvv', e.target.value)}
                          placeholder="123"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre en la Tarjeta
                      </label>
                      <input
                        type="text"
                        value={cardData.name}
                        onChange={(e) => handleCardChange('name', e.target.value)}
                        placeholder="Juan Pérez"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="p-6 bg-gray-50 flex space-x-4">
                    <button
                      onClick={() => setCurrentView('quote')}
                      className="flex-1 bg-gray-300 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-400 transition-colors"
                    >
                      Volver
                    </button>
                    
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handlePayment}
                      className="flex-1 bg-gradient-to-r from-orange-500 to-red-600 text-white py-3 px-6 rounded-lg font-medium hover:shadow-lg transition-all duration-300 flex items-center justify-center"
                    >
                      <ArrowRight className="w-5 h-5 mr-2" />
                      Procesar Pago
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Vista de Confirmación */}
            {currentView === 'confirmed' && (
              <motion.div
                key="confirmed"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="text-center max-w-2xl mx-auto"
              >
                <motion.div
                  className="w-32 h-32 bg-gradient-to-r from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-8 shadow-2xl"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                >
                  <CheckCircle className="w-16 h-16 text-white" />
                </motion.div>

                <h2 className="text-4xl font-bold text-gray-800 mb-4 bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
                  ¡Pago Confirmado!
                </h2>

                <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
                  <p className="text-xl text-gray-600 mb-6">
                    Tu pago ha sido procesado exitosamente. ¡Ya tienes tu reservación confirmada!
                  </p>
                  
                  <div className="bg-green-50 rounded-lg p-4 mb-6">
                    <h3 className="font-bold text-green-800 mb-2">Detalles de tu Reservación:</h3>
                    <p className="text-green-700">Destino: {quoteData?.destination}</p>
                    <p className="text-green-700">Fecha: {quoteData?.date}</p>
                    <p className="text-green-700">Personas: {quoteData?.persons}</p>
                    <p className="text-green-700">Total Pagado: ${quoteData?.total.toLocaleString()}</p>
                  </div>

                  <p className="text-sm text-gray-500">
                    Recibirás un email de confirmación en los próximos minutos con todos los detalles de tu viaje.
                  </p>
                </div>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={resetSystem}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-4 rounded-full text-lg font-semibold shadow-xl hover:shadow-2xl transition-all duration-300"
                >
                  Realizar Nueva Búsqueda
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default QuoteVerificationSystem;